import React from "react";
import { DeleteConfirmationModal } from "../../../designSystem/compositeComponents/DeleteConfirmationButton";
import { ActionPointListPanel } from "../../actionPoints/ActionPointList";
import { moveAgendaItem, updateAgendaItem, deleteAgendaItem, createAgendaItem } from "../../../api/activityClientApi";
import { ActionPointView, AgendaItem, PublicAmorgUser, fieldValidation, agendaItemHelper, stringUtil, rng } from "../../../amorg-common";
import { Flex, FlexItem } from "../../../designSystem/compositeComponents/FlexHelpers";
import { Divider } from "../../../designSystem/Divider";
import { EditableText } from "../../../designSystem/compositeComponents/EditableText";
import { Button } from "../../../designSystem/button/Button";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { MessageCard } from "../../../designSystem/Message";
import { TooltipWrapper } from '../../../designSystem/TooltipWrapper';
import { Icon } from '../../../designSystem/icons/Icons';
import { colors } from "../../../designSystem/colors/color";
import { EditableTextArea } from "../../../designSystem/compositeComponents/EditableTextArea";
import { Label } from "../../../designSystem/Label";
import { ToolBarButton, ToolBarContainer } from '../../../designSystem/compositeComponents/ButtonToolBar';
import { AnimatedCollapsibleContent } from "../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { ListRow } from "../../../designSystem/compositeComponents/ListRow";
import styled from "styled-components";
import { TextAvatar } from '../../../designSystem/avatar/TextAvatar';

type AgendaItemListPanelProps = {
    activityId: string,
    agendaItems: AgendaItem[],
    users: PublicAmorgUser[],
    onUpdated: () => void;
};
export function AgendaItemListPanel (props: AgendaItemListPanelProps): JSX.Element
{
    const [expandedIds, setExpandedIds] = React.useState<string[]>([]);

    React.useEffect(() =>
    {
        // Remove expanded ids that are no longer in the list
        if (expandedIds.some(id => props.agendaItems.every(ai => ai.id !== id)))
            setExpandedIds(expandedIds.filter(id => props.agendaItems.some(ai => ai.id === id)));
    }, props.agendaItems);

    async function handleClickCreateAgendaItem ()
    {
        const nameBase = 'Nyt agendapunkt';
        const nextTitle = stringUtil.generateNextSequenceName(nameBase, props.agendaItems.map(ap => ap.title));
        const id = rng.getRandomId();

        const result = await createAgendaItem(id, props.activityId, nextTitle, '', '');
        if (result.isSuccess)
        {
            props.onUpdated();
            setExpandedIds([id]);
        }
    }

    const sorted = agendaItemHelper.getSortedAgendaItems(props.agendaItems);

    return (
        <Flex gap={24} flexDirection="column">
            <ButtonRow variant={"stretched"}>
                <Button
                    icon="actions_plus"
                    variant="filled"
                    size='magnum'
                    text="Tilføj agendapunkt"
                    onClick={handleClickCreateAgendaItem}
                />
                {/* <CreateAgendaItemModalButton activityId={props.activityId} onAgendaItemCreated={props.onUpdated} /> */}
            </ButtonRow>

            {sorted.length === 0 &&
                <Flex flexDirection="column">
                    <FlexItem alignSelf="center">
                        <MessageCard variant="info">
                            Agendapunkter hjælper med at skabe struktur på mødet, så tiden bliver brugt bedst muligt, og gør det nemt at dokumentere og følge op på diskussioner og vedtagne handlepunkter.
                        </MessageCard>
                    </FlexItem>

                </Flex>
            }

            <Flex flexDirection="column" gap={32}>
                {sorted.map((ai,i) =>
                    <AgendaItemPanel key={ai.id}
                        activityId={props.activityId}
                        agendaItem={ai}
                        agendaItemIndex={i}
                        agendaItemsCount={props.agendaItems.length}
                        users={props.users}
                        expanded={expandedIds.includes(ai.id)}
                        onUpdated={props.onUpdated}
                        onToggleExpand={(id, expand) =>
                        {
                            if (expand)
                                setExpandedIds([...expandedIds, id]);
                            else
                                setExpandedIds(expandedIds.filter(tId => tId !== id));

                        }} />
                )}
            </Flex>

        </Flex>
    );
}

type AgendaItemPanelProps = {
    activityId: string,
    agendaItem: AgendaItem,
    agendaItemIndex: number,
    agendaItemsCount: number,
    users: PublicAmorgUser[],
    expanded: boolean,
    onUpdated: () => void,
    onToggleExpand: (id: string, expand: boolean) => void;
};


function AgendaItemPanel (props: AgendaItemPanelProps): JSX.Element
{
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const [inDeleteAgendaItem, setInDeleteAgendaItem] = React.useState<boolean>(false);
    const [inEditActionPoint, setInEditActionPoint] = React.useState<ActionPointView>(null);
    const [lastCreatedId, setLastCreatedId] = React.useState('');

    React.useEffect(() =>
    {
        if (inEditActionPoint)
        {
            const refreshedActionPoint = props.agendaItem.actionPoints.find(ap => ap.id === inEditActionPoint.id);
            if (refreshedActionPoint)
                setInEditActionPoint(refreshedActionPoint);
            else
                setInEditActionPoint(null);

        }
        else
        {
            if (lastCreatedId)
            {
                const ap = props.agendaItem.actionPoints.find(ap => ap.id === lastCreatedId);
                if (ap)
                {
                    setInEditActionPoint(ap);
                    setLastCreatedId('');
                }
            }
        }
    }, [props.agendaItem]);

    async function handleMoveAgendaItem (up: boolean)
    {
        await moveAgendaItem(props.agendaItem.id, props.activityId, up ? 'up' : 'down');
        props.onUpdated();
    }

    async function handleUpdateTitle (value)
    {
        await updateAgendaItem(props.agendaItem.id, props.activityId, value, props.agendaItem.description, props.agendaItem.note);
        props.onUpdated();
    }

    async function handleUpdateDescription (value)
    {
        await updateAgendaItem(props.agendaItem.id, props.activityId, props.agendaItem.title, value, props.agendaItem.note);
        props.onUpdated();
    }

    async function handleUpdateNote (value)
    {
        await updateAgendaItem(props.agendaItem.id, props.activityId, props.agendaItem.title, props.agendaItem.description, value);
        props.onUpdated();
    }

    async function handleDeleteAgendaItem ()
    {
        await deleteAgendaItem(props.agendaItem.id, props.activityId);
        setInDeleteAgendaItem(false);
        props.onUpdated();
    }

    const aps = props.agendaItem.actionPoints;
    const deadlineExceeded = aps.filter(ap => ap.deadline && new Date(ap.deadline) < new Date());
    let actionPointToolTip = { text: 'Ingen handlepunkter oprettet', color: colors.icon.passive };
    if (aps.length > 0)
    {
        if (aps.every(ap => ap.done))
            actionPointToolTip = {
                text: `Alle handlepunkter gennemført`, color: colors.icon.success
            };
        else if (deadlineExceeded.length > 0)
            actionPointToolTip = { text: `Deadline overskredet for ${deadlineExceeded.length} handlepunkt(er)`, color: colors.icon.error };
        else
            actionPointToolTip = { text: '', color: colors.icon.active };
    }

    return (
        <Flex flexDirection="column">

            <ListRow styleVariant="neutral" expanded={expanded} alignItems="flex-start">
                <Flex flexDirection="column" gap={4}>
                    <Flex alignItems="center">
                        <TextAvatar text={`${props.agendaItemIndex + 1}`} size="32px" />
                        
                        <div style={{ flexGrow: 1 }}>
                            <EditableText
                                ariaLabel="Agendapunkt titel"
                                value={props.agendaItem.title}
                                onSave={async (value) =>
                                {
                                    if (fieldValidation.agendaItem.title(value))
                                        await handleUpdateTitle(value);
                                }}
                                validator={(currentValue) =>
                                {
                                    if (fieldValidation.agendaItem.title(currentValue))
                                        return { valid: true };
                                    return { valid: false, errorMessage: `Ugyldig titel. Skal udfyldes med maks. 100 (${currentValue.length}) tegn.` };
                                }}
                                styleMode='H3'
                                placeholder="Indtast titel" />

                        </div>
                        <Flex justifyContent="flex-end">
                            <ToolBarContainer>
                                <ToolBarButton variant="text" icon="caret_caretUp" tooltip="Flyt op" onClick={() => handleMoveAgendaItem(true)} />
                                <ToolBarButton variant="text" icon="caret_caretDown" tooltip="Flyt ned" onClick={() => handleMoveAgendaItem(false)} />
                                <ToolBarButton variant="destructive-text" icon="interface_actions_delete" tooltip="Slet agendapunkt" onClick={() => setInDeleteAgendaItem(true)} />
                                <DeleteConfirmationModal
                                    buttonText={'Slet agendapunkt'}
                                    confirmationHeading="Slet agendapunkt?"
                                    confirmationText="Er du sikker på du ønsker at slette agendapunktet? Tilknyttede handlepunkter vil ligeledes blive slettet."
                                    requireKey={false}
                                    onConfirmDelete={handleDeleteAgendaItem}
                                    showModal={inDeleteAgendaItem}
                                    onCancel={() => setInDeleteAgendaItem(false)} />
                            </ToolBarContainer>
                        </Flex>
                    </Flex>

                    <EditableTextArea
                        ariaLabel="Agendapunkt beskrivelse"
                        value={props.agendaItem.description}
                        placeholder="Tilføj beskrivelse..."
                        validator={(currentValue) =>
                            fieldValidation.agendaItem.description(currentValue)
                                ? { valid: true }
                                : { valid: false, errorMessage: `Beskrivelsen må ikke overstige 10000 tegn. (${currentValue.length})` }
                        }
                        onSave={(value, isValid) =>
                        {
                            if (isValid)
                            {
                                handleUpdateDescription(value);
                            }
                        }} />

                    <DetailsPanel>
                        <Flex justifyContent="space-between">
                            <Flex gap={8}>
                                <Flex alignItems="center">
                                    <TooltipWrapper tooltip={actionPointToolTip.text}>
                                        <Icon name="interface_glyph_checked" size="small"
                                            color={actionPointToolTip.color} />
                                    </TooltipWrapper>
                                    <div>
                                        {aps.length === 0 ? 'Ingen handlepunkter' : aps.length === 1 ? '1 handlepunkt' : `${aps.length} handlepunkter`}
                                    </div>
                                </Flex>
                                <Flex alignItems="center">
                                    <TooltipWrapper tooltip={props.agendaItem.note.length === 0 ? 'Intet referat tilføjet' : 'Referat er tilføjet'}>
                                        <Icon name="interface_glyph_comment" size="small"
                                            color={props.agendaItem.note === '' ? colors.icon.passive : colors.icon.active} />
                                    </TooltipWrapper>
                                    <div>
                                        {props.agendaItem.note.length === 0 ? 'Intet referat' : 'Referat tilføjet'}
                                    </div>
                                </Flex>
                            </Flex>

                            <Button
                                variant="text"
                                // text={expanded ? 'Skjul' : 'Vis'}
                                icon={expanded ? 'arrows_chevron_chevronUp' : 'arrows_chevron_chevronDown'}
                                onClick={() => setExpanded(!expanded)}
                            />

                        </Flex>
                    </DetailsPanel>

                    <AnimatedCollapsibleContent open={expanded}>


                        <>
                            <Divider />

                            <Flex flexDirection="column" gap={8}>
                                <Flex flexDirection="column">
                                    <Label>Referat</Label>
                                    <EditableTextArea
                                        ariaLabel="Agendapunkt referat"
                                        value={props.agendaItem.note}
                                        placeholder="Tilføj referat..."
                                        validator={(currentValue) =>
                                            fieldValidation.agendaItem.description(currentValue)
                                                ? { valid: true }
                                                : { valid: false, errorMessage: `Referatet må ikke overstige 10000 tegn. (${currentValue.length})` }
                                        }
                                        onSave={(value) =>
                                        {
                                            if (fieldValidation.agendaItem.note(value))
                                            {
                                                handleUpdateNote(value);
                                            }
                                        }} />
                                </Flex>

                                <Divider />

                                <ActionPointListPanel
                                    mode={ { type: 'agendaItem', agendaItemId: props.agendaItem.id } }
                                    users={props.users}
                                    actionPoints={props.agendaItem.actionPoints.sort((a, b) => a.created < b.created ? -1 : 1)}
                                    onUpdated={props.onUpdated} />

                            </Flex>
                        </>
                    </AnimatedCollapsibleContent>

                </Flex>
            </ListRow>
        </Flex>
    );
}

const DetailsPanel = styled.div`
    background: ${props => props.theme.colors.card.light.default.background};
    padding: 0 0 0 8px;
`;